import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  isRtlLocale,
  type CurrencyCode,
  type DashboardTrendBucket,
  type DashboardTrendPoint,
} from "@ticketnjoy/types";

import { ISTANBUL_TZ, useFormat, type Format } from "@/lib/format";
import { useLocale, useT } from "@/lib/i18n";

interface TrendChartProps {
  points: readonly DashboardTrendPoint[];
  bucket: DashboardTrendBucket;
  currency: CurrencyCode;
}

/**
 * Bucket label formatlayıcı: saat kovasında saat, gün kovasında kısa tarih (İstanbul).
 */
function formatBucket(fmt: Format, iso: string, bucket: DashboardTrendBucket): string {
  if (bucket === "hour") {
    return fmt.time(iso, { hour: "2-digit", minute: "2-digit", timeZone: ISTANBUL_TZ });
  }
  return fmt.date(iso, { day: "2-digit", month: "short", timeZone: ISTANBUL_TZ });
}

export function TrendChart({ points, bucket, currency }: TrendChartProps) {
  const t = useT("reportCards.trend");
  const fmt = useFormat();
  const isRtl = isRtlLocale(useLocale());
  const data = useMemo(
    () =>
      points.map((p) => ({
        bucketLabel: formatBucket(fmt, p.bucketUtc, bucket),
        revenue: p.revenue,
        ticketsSold: p.ticketsSold,
      })),
    [fmt, points, bucket],
  );

  if (data.length === 0) {
    return (
      <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
        {t("empty")}
      </div>
    );
  }

  return (
    <div className="h-[260px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 12, right: isRtl ? 0 : 8, left: isRtl ? 8 : 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="bucketLabel"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            reversed={isRtl}
          />
          <YAxis
            orientation={isRtl ? "right" : "left"}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) =>
              fmt.number(v, { notation: "compact", maximumFractionDigits: 1 })
            }
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload || payload.length === 0) return null;
              const point = payload[0]?.payload as
                | (typeof data)[number]
                | undefined;
              if (!point) return null;
              return (
                <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
                  <div className="mb-1 font-medium text-foreground">
                    {label}
                  </div>
                  <div className="text-muted-foreground">
                    {t("revenue")}:{" "}
                    <span className="font-medium text-foreground">
                      {fmt.money(point.revenue, currency)}
                    </span>
                  </div>
                  <div className="text-muted-foreground">
                    {t("tickets")}:{" "}
                    <span className="font-medium text-foreground">
                      {fmt.number(point.ticketsSold)}
                    </span>
                  </div>
                </div>
              );
            }}
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#revenueGradient)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
